import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, Check, X, AlertCircle, FileText, Shield,
  Ban, CreditCard, Eye, Image as ImageIcon,
} from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetDealer, adminUpdateDealerStatus } from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft:          'text-zinc-500 bg-zinc-800 border-zinc-700',
  submitted:      'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  under_review:   'text-blue-400 bg-blue-500/10 border-blue-500/20',
  approved:       'text-green-400 bg-green-500/10 border-green-500/20',
  rejected:       'text-red-400 bg-red-500/10 border-red-500/20',
  info_requested: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  suspended:      'text-red-500 bg-red-900/20 border-red-900/40',
};

// ── Document section — image thumbnails with prominent labels ─────────────────
const DOC_CFG = {
  ffl:   { label: 'FFL LICENSE CARD',   color: 'text-orange-400', border: 'border-orange-500/50', bg: 'bg-orange-500/8',  icon: Shield,   badge: 'bg-orange-500 text-white' },
  sot:   { label: 'SOT REGISTRATION',   color: 'text-purple-400', border: 'border-purple-500/50', bg: 'bg-purple-500/8',  icon: FileText, badge: 'bg-purple-500 text-white' },
  other: { label: 'OTHER CREDENTIAL',   color: 'text-blue-400',   border: 'border-blue-500/30',   bg: 'bg-blue-500/5',    icon: FileText, badge: 'bg-blue-500 text-white' },
};

function DocGroup({ docType, docs }) {
  const cfg = DOC_CFG[docType] || DOC_CFG.other;
  const Icon = cfg.icon;
  if (docs.length === 0) return null;

  const isImage = (name) => /\.(jpg|jpeg|png|webp|heic)$/i.test(name || '');

  return (
    <div className={`border ${cfg.border} ${cfg.bg} overflow-hidden mb-3`}>
      {/* Prominent section label */}
      <div className={`flex items-center gap-2 px-4 py-3 border-b ${cfg.border}`}>
        <span className={`flex items-center gap-1.5 ${cfg.badge} font-bebas text-sm tracking-widest px-3 py-1`}>
          <Icon size={13} />{cfg.label}
        </span>
        <span className="font-inter text-[10px] text-zinc-600">{docs.length} document{docs.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Thumbnails grid */}
      <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {docs.map((doc, i) => (
          <a
            key={i}
            href={`${BASE}${doc.url}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`dealer-doc-${docType}-${i}`}
            className="group relative bg-[#111] border border-white/10 hover:border-orange-500/40 overflow-hidden transition-all"
          >
            {/* Thumbnail */}
            <div className="aspect-[4/3] flex items-center justify-center overflow-hidden bg-[#0f0f0f]">
              {isImage(doc.filename || doc.url) ? (
                <img
                  src={`${BASE}${doc.url}`}
                  alt={cfg.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={e => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`${isImage(doc.filename || doc.url) ? 'hidden' : 'flex'} flex-col items-center justify-center p-4 w-full h-full`}>
                <FileText size={28} className={cfg.color} />
                <span className="font-inter text-[10px] text-zinc-600 mt-1 text-center">PDF Document</span>
              </div>
            </div>
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex items-center gap-1 bg-orange-500 text-white font-inter text-xs px-3 py-1.5">
                <Eye size={12} />View Full
              </div>
            </div>
            {/* Label footer */}
            <div className="px-2 py-1.5 border-t border-white/5">
              <p className="font-inter text-[9px] text-zinc-500 truncate">
                {doc.original_name || doc.filename || 'Document'}
              </p>
              <p className="font-inter text-[8px] text-zinc-700">
                {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Payment Rule Selector ─────────────────────────────────────────────────────
function PaymentRuleSelector({ dealer, token, onUpdated }) {
  const [rules, setRules] = useState([]);
  const [selected, setSelected] = useState(dealer.payment_rule_id || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/admin/payment-rules`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setRules(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [token]);

  const handleChange = async (ruleId) => {
    setSelected(ruleId);
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/admin/dealers/${dealer._id}/payment-rule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_rule_id: ruleId || null }),
      });
      if (res.ok) { onUpdated(); }
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const currentRule = rules.find(r => r.rule_id === selected);

  return (
    <div className="bg-[#1a1a1a] border border-white/10 p-5">
      <h2 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
        <CreditCard size={16} className="text-orange-500" />PAYMENT RULE
      </h2>
      <select
        data-testid="payment-rule-select"
        value={selected}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors mb-3"
      >
        <option value="">— No rule assigned (default invoice) —</option>
        {rules.map(r => (
          <option key={r.rule_id} value={r.rule_id}>
            {r.name} ({r.type_label})
          </option>
        ))}
      </select>
      {currentRule && (
        <div className="bg-orange-500/5 border border-orange-500/20 px-4 py-3">
          <p className="font-bebas text-sm text-orange-400 tracking-wider">{currentRule.name}</p>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">{currentRule.type_label}</p>
          {currentRule.deposit_percent && <p className="font-inter text-xs text-zinc-600">Deposit: {currentRule.deposit_percent}%</p>}
          {currentRule.net_days && <p className="font-inter text-xs text-zinc-600">Net-{currentRule.net_days} terms</p>}
        </div>
      )}
      {saving && <p className="font-inter text-xs text-zinc-500 mt-2">Saving...</p>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminDealerDetail() {
  const { id } = useParams();
  const { token } = useAdminAuth();
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    adminGetDealer(token, id)
      .then(setDealer)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, token]);

  const handleStatusUpdate = async () => {
    if (!action) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await adminUpdateDealerStatus(token, id, action, note);
      setDealer(updated);
      setSuccess(`Dealer status updated to ${action}`);
      setAction(''); setNote('');
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!dealer) return <div className="text-red-400 font-inter text-sm p-4">{error || 'Dealer not found'}</div>;

  // Group documents by type
  const fflDocs =   (dealer.documents || []).filter(d => d.document_type === 'ffl');
  const sotDocs =   (dealer.documents || []).filter(d => d.document_type === 'sot');
  const otherDocs = (dealer.documents || []).filter(d => d.document_type !== 'ffl' && d.document_type !== 'sot');
  const totalDocs = (dealer.documents || []).length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/dealers" className="text-zinc-500 hover:text-white transition-colors">
          <ChevronLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="font-bebas text-3xl text-white tracking-wider">{dealer.business_name}</h1>
          <span className={`font-inter text-xs font-bold px-2 py-0.5 border uppercase tracking-wider ${STATUS_COLORS[dealer.status] || STATUS_COLORS.draft}`}>
            {dealer.status?.replace('_', ' ')}
          </span>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}
      {success && <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3 mb-4">{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: dealer info + documents */}
        <div className="lg:col-span-2 space-y-4">
          {/* Business info */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-3">BUSINESS INFORMATION</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Business Name', value: dealer.business_name },
                { label: 'Contact', value: `${dealer.first_name || ''} ${dealer.last_name || ''}`.trim() },
                { label: 'Email', value: dealer.email },
                { label: 'Phone', value: dealer.phone || '—' },
                { label: 'Address', value: [dealer.business_address, dealer.city, dealer.state, dealer.zip_code].filter(Boolean).join(', ') || '—' },
                { label: 'Tax ID', value: dealer.tax_id || '—' },
              ].map(f => (
                <div key={f.label}>
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                  <p className="font-inter text-sm text-white">{f.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FFL & SOT info */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-3 flex items-center gap-2">
              <Shield size={16} className="text-orange-500" />FFL &amp; LICENSING
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'FFL Number', value: dealer.ffl_number },
                { label: 'FFL Expiry', value: dealer.ffl_expiry },
                { label: 'SOT Status', value: dealer.sot_status === 'yes' ? 'Active SOT ✓' : dealer.sot_status || '—' },
                { label: 'SOT Class', value: dealer.sot_status === 'yes' ? 'Class 3 — Dealer' : '—' },
              ].map(f => (
                <div key={f.label}>
                  <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                  <p className={`font-inter text-sm ${f.value?.includes('✓') ? 'text-green-400' : 'text-white'}`}>{f.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Documents — image thumbnails with prominent labels */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4 flex items-center gap-2">
              <ImageIcon size={16} className="text-orange-500" />
              LICENSE DOCUMENTS
              <span className="font-inter text-xs font-normal text-zinc-500 normal-case tracking-normal ml-1">
                ({totalDocs} file{totalDocs !== 1 ? 's' : ''})
              </span>
            </h2>

            {totalDocs === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-zinc-800">
                <ImageIcon size={28} className="text-zinc-700 mx-auto mb-2" />
                <p className="font-inter text-sm text-zinc-600">No documents uploaded yet</p>
                <p className="font-inter text-xs text-zinc-700 mt-1">
                  Dealer can upload from their portal → Profile page
                </p>
              </div>
            ) : (
              <>
                <DocGroup docType="ffl"   docs={fflDocs} />
                <DocGroup docType="sot"   docs={sotDocs} />
                <DocGroup docType="other" docs={otherDocs} />
              </>
            )}
          </div>

          {/* Admin note */}
          {dealer.admin_note && (
            <div className="bg-[#1a1a1a] border border-white/10 p-5">
              <h2 className="font-bebas text-lg text-white tracking-wider mb-2">ADMIN NOTE</h2>
              <p className="font-inter text-sm text-zinc-400">{dealer.admin_note}</p>
              {dealer.reviewed_by && (
                <p className="font-inter text-xs text-zinc-600 mt-2">By: {dealer.reviewed_by} · {dealer.reviewed_at ? new Date(dealer.reviewed_at).toLocaleDateString() : ''}</p>
              )}
            </div>
          )}
        </div>

        {/* Right: actions + payment rule + timeline */}
        <div className="space-y-4">
          {/* Payment Rule */}
          <PaymentRuleSelector dealer={dealer} token={token} onUpdated={load} />

          {/* Review Decision */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">REVIEW DECISION</h2>
            <div className="space-y-2 mb-4">
              {[
                { val: 'approved',       label: 'Approve Account',     icon: Check,        color: 'border-green-500/50 text-green-400 hover:bg-green-500/10' },
                { val: 'under_review',   label: 'Mark Under Review',   icon: AlertCircle,  color: 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' },
                { val: 'info_requested', label: 'Request More Info',   icon: AlertCircle,  color: 'border-orange-500/50 text-orange-400 hover:bg-orange-500/10' },
                { val: 'rejected',       label: 'Reject Application',  icon: X,            color: 'border-red-500/50 text-red-400 hover:bg-red-500/10' },
                { val: 'suspended',      label: 'Suspend Account',     icon: Ban,          color: 'border-red-900/50 text-red-500 hover:bg-red-900/20' },
              ].map(a => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.val}
                    data-testid={`action-${a.val}`}
                    onClick={() => setAction(action === a.val ? '' : a.val)}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 border font-inter text-sm font-medium transition-colors ${
                      action === a.val ? 'border-white/30 bg-white/10 text-white' : `border-white/10 ${a.color}`
                    }`}
                  >
                    <Icon size={14} />{a.label}
                  </button>
                );
              })}
            </div>
            {action && (
              <div className="space-y-3">
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">
                    {action === 'suspended' ? 'Reason for suspension' : 'Note to Dealer (optional)'}
                  </label>
                  <textarea
                    data-testid="admin-note-input"
                    rows={3}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors resize-none placeholder-zinc-600"
                    placeholder={action === 'suspended' ? 'Reason for account suspension...' : action === 'info_requested' ? 'Explain what information is needed...' : 'Optional note...'}
                  />
                </div>
                <button
                  data-testid="confirm-status-button"
                  onClick={handleStatusUpdate}
                  disabled={saving}
                  className={`w-full disabled:opacity-60 text-white font-bebas text-base tracking-widest py-3 transition-colors ${
                    action === 'suspended' ? 'bg-red-700 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {saving ? 'SAVING...' : `CONFIRM: ${action.replace('_', ' ').toUpperCase()}`}
                </button>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h2 className="font-bebas text-lg text-white tracking-wider mb-3">TIMELINE</h2>
            <div className="space-y-3">
              {[
                { label: 'Application Created', date: dealer.created_at },
                { label: 'Submitted', date: dealer.submitted_at },
                { label: 'Last Reviewed', date: dealer.reviewed_at },
                { label: 'Approved', date: dealer.approved_at },
              ].filter(t => t.date).map(t => (
                <div key={t.label} className="flex items-center justify-between">
                  <span className="font-inter text-xs text-zinc-500">{t.label}</span>
                  <span className="font-inter text-xs text-white">{new Date(t.date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
